import React, { useState, useEffect } from 'react';
import { Search, Plus, X, TrendingUp } from 'lucide-react';
import { productsAPI } from '../services/api';

const Compare = () => {
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [comparedProducts, setComparedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, [searchTerm]);

  const fetchProducts = async () => {
    try {
      const response = await productsAPI.getAll({ search: searchTerm });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const addProductToCompare = (product) => {
    if (selectedProducts.length < 3 && !selectedProducts.find(p => p.id === product.id)) {
      setSelectedProducts([...selectedProducts, product]);
      if (selectedProducts.length === 2) {
        setShowSearch(false);
      }
    }
  };

  const removeProductFromCompare = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
    setShowSearch(true);
  };

  const compareProducts = async () => {
    if (selectedProducts.length >= 2) {
      try {
        const productIds = selectedProducts.map(p => p.id);
        const response = await productsAPI.compare(productIds);
        setComparedProducts(response.data);
      } catch (error) {
        console.error('Error comparing products:', error);
      }
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-[#0A1128] to-[#001F54] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4">Comparar Produtos</h1>
          <p className="text-gray-300 text-lg">Compare até 3 celulares lado a lado</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Selected Products */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-[#0A1128]">
              Produtos Selecionados ({selectedProducts.length}/3)
            </h2>
            {selectedProducts.length >= 2 && (
              <button
                onClick={compareProducts}
                className="bg-gradient-to-r from-[#0353A4] to-[#006BA6] text-white px-6 py-2 rounded-lg font-bold hover:from-[#001F54] hover:to-[#0353A4] transition-all"
                data-testid="compare-button"
              >
                Comparar
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[0, 1, 2].map((index) => (
              <div key={index} className="bg-white rounded-xl shadow-md p-6 min-h-[200px]">
                {selectedProducts[index] ? (
                  <div className="relative">
                    <button
                      onClick={() => removeProductFromCompare(selectedProducts[index].id)}
                      className="absolute top-0 right-0 text-red-500 hover:text-red-700"
                    >
                      <X className="h-5 w-5" />
                    </button>
                    <img
                      src={selectedProducts[index].images?.[0] || ''}
                      alt={selectedProducts[index].model}
                      className="w-full h-32 object-contain mb-4"
                    />
                    <h3 className="font-bold text-[#0A1128]">
                      {selectedProducts[index].brand}
                    </h3>
                    <p className="text-gray-600 text-sm">{selectedProducts[index].model}</p>
                    <p className="text-[#0353A4] font-bold mt-2">
                      {formatPrice(selectedProducts[index].price)}
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    <div className="text-center">
                      <Plus className="h-12 w-12 mx-auto mb-2" />
                      <p className="text-sm">Selecione um produto</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Search Products */}
        {showSearch && selectedProducts.length < 3 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-[#0A1128] mb-4">Buscar Produtos</h2>
            
            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por marca ou modelo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0353A4] focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-h-96 overflow-y-auto">
              {products
                .filter(p => !selectedProducts.find(sp => sp.id === p.id))
                .map(product => (
                  <button
                    key={product.id}
                    onClick={() => addProductToCompare(product)}
                    className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow text-left"
                  >
                    <img
                      src={product.images?.[0] || ''}
                      alt={product.model}
                      className="w-full h-32 object-contain mb-3"
                    />
                    <h3 className="font-bold text-[#0A1128] text-sm">{product.brand}</h3>
                    <p className="text-gray-600 text-xs mb-2">{product.model}</p>
                    <p className="text-[#0353A4] font-bold">{formatPrice(product.price)}</p>
                  </button>
                ))}
            </div>
          </div>
        )}

        {/* Comparison Table */}
        {comparedProducts.length >= 2 && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-[#0A1128] to-[#001F54] text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-bold">Especificação</th>
                    {comparedProducts.map(product => (
                      <th key={product.id} className="px-6 py-4 text-center">
                        <div>
                          <p className="font-bold">{product.brand}</p>
                          <p className="text-sm text-gray-300">{product.model}</p>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Images */}
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 font-semibold text-gray-700">Imagem</td>
                    {comparedProducts.map(product => (
                      <td key={product.id} className="px-6 py-4 text-center">
                        <img
                          src={product.images?.[0] || ''}
                          alt={product.model}
                          className="w-32 h-32 object-contain mx-auto"
                        />
                      </td>
                    ))}
                  </tr>

                  {/* Price */}
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <td className="px-6 py-4 font-semibold text-gray-700">Preço</td>
                    {comparedProducts.map(product => (
                      <td key={product.id} className="px-6 py-4 text-center">
                        <p className="text-2xl font-bold text-[#0353A4]">
                          {formatPrice(product.price)}
                        </p>
                        {product.market_price && product.market_price > product.price && (
                          <div className="mt-2">
                            <p className="text-sm text-gray-500 line-through">
                              {formatPrice(product.market_price)}
                            </p>
                            <div className="inline-flex items-center space-x-1 bg-red-100 text-red-600 px-2 py-1 rounded text-xs font-semibold mt-1">
                              <TrendingUp className="h-3 w-3" />
                              <span>-{Math.round((1 - product.price / product.market_price) * 100)}%</span>
                            </div>
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>

                  {/* Condition */}
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 font-semibold text-gray-700">Condição</td>
                    {comparedProducts.map(product => (
                      <td key={product.id} className="px-6 py-4 text-center">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          product.condition === 'new' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {product.condition === 'new' ? 'Novo' : 'Usado'}
                        </span>
                      </td>
                    ))}
                  </tr>

                  {/* Stock */}
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <td className="px-6 py-4 font-semibold text-gray-700">Estoque</td>
                    {comparedProducts.map(product => (
                      <td key={product.id} className="px-6 py-4 text-center">
                        <span className={product.stock > 0 ? 'text-green-600' : 'text-red-600'}>
                          {product.stock > 0 ? `${product.stock} disponíveis` : 'Sem estoque'}
                        </span>
                      </td>
                    ))}
                  </tr>

                  {/* Specifications */}
                  {comparedProducts.some(p => p.specifications?.screen) && (
                    <tr className="border-b border-gray-200">
                      <td className="px-6 py-4 font-semibold text-gray-700">Tela</td>
                      {comparedProducts.map(product => (
                        <td key={product.id} className="px-6 py-4 text-center text-gray-600">
                          {product.specifications?.screen || 'N/A'}
                        </td>
                      ))}
                    </tr>
                  )}

                  {comparedProducts.some(p => p.specifications?.processor) && (
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <td className="px-6 py-4 font-semibold text-gray-700">Processador</td>
                      {comparedProducts.map(product => (
                        <td key={product.id} className="px-6 py-4 text-center text-gray-600">
                          {product.specifications?.processor || 'N/A'}
                        </td>
                      ))}
                    </tr>
                  )}

                  {comparedProducts.some(p => p.specifications?.ram) && (
                    <tr className="border-b border-gray-200">
                      <td className="px-6 py-4 font-semibold text-gray-700">RAM</td>
                      {comparedProducts.map(product => (
                        <td key={product.id} className="px-6 py-4 text-center text-gray-600">
                          {product.specifications?.ram || 'N/A'}
                        </td>
                      ))}
                    </tr>
                  )}

                  {comparedProducts.some(p => p.specifications?.storage) && (
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <td className="px-6 py-4 font-semibold text-gray-700">Armazenamento</td>
                      {comparedProducts.map(product => (
                        <td key={product.id} className="px-6 py-4 text-center text-gray-600">
                          {product.specifications?.storage || 'N/A'}
                        </td>
                      ))}
                    </tr>
                  )}

                  {comparedProducts.some(p => p.specifications?.camera) && (
                    <tr className="border-b border-gray-200">
                      <td className="px-6 py-4 font-semibold text-gray-700">Câmera</td>
                      {comparedProducts.map(product => (
                        <td key={product.id} className="px-6 py-4 text-center text-gray-600">
                          {product.specifications?.camera || 'N/A'}
                        </td>
                      ))}
                    </tr>
                  )}

                  {comparedProducts.some(p => p.specifications?.battery) && (
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <td className="px-6 py-4 font-semibold text-gray-700">Bateria</td>
                      {comparedProducts.map(product => (
                        <td key={product.id} className="px-6 py-4 text-center text-gray-600">
                          {product.specifications?.battery || 'N/A'}
                        </td>
                      ))}
                    </tr>
                  )}

                  {comparedProducts.some(p => p.specifications?.os) && (
                    <tr className="border-b border-gray-200">
                      <td className="px-6 py-4 font-semibold text-gray-700">Sistema Operacional</td>
                      {comparedProducts.map(product => (
                        <td key={product.id} className="px-6 py-4 text-center text-gray-600">
                          {product.specifications?.os || 'N/A'}
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Compare;